import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";
import { usePagesTranslation } from "../../locales";

/** Mini board letters — “ROUTE” on row 2 for the drag path. */
const DEMO_GRID = [
    ["P", "L", "A", "Z", "A", "K"],
    ["V", "E", "N", "U", "E", "I"],
    ["R", "O", "U", "T", "E", "S"],
    ["C", "U", "R", "B", "G", "O"],
    ["D", "E", "T", "O", "U", "R"],
    ["B", "O", "O", "T", "H", "N"],
];

/** Cells that spell ROUTE (row 2, cols 0–4). */
const ROUTE_CELLS = [
    { row: 2, col: 0 },
    { row: 2, col: 1 },
    { row: 2, col: 2 },
    { row: 2, col: 3 },
    { row: 2, col: 4 },
];

const CELL = 36;
const GAP = 3;

const sweep = keyframes`
  0% { opacity: 0.15; transform: scale(0.92); }
  20% { opacity: 1; transform: scale(1); }
  55% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.35; transform: scale(0.96); }
`;

const fingerMove = keyframes`
  0% { left: ${CELL / 2}px; opacity: 0; }
  8% { opacity: 1; }
  85% { opacity: 1; }
  100% { left: ${4 * (CELL + GAP) + CELL / 2}px; opacity: 0.85; }
`;

/**
 * Non-interactive Mini 6×6 demo that loops a drag-select along “ROUTE”.
 */
export default function HowToWordSearchDragDemo() {
    const { t } = usePagesTranslation();
    const pathTop = 2 * (CELL + GAP);
    const pathLeft = 0;
    const pathWidth = 5 * CELL + 4 * GAP;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                py: 1,
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#1b5e20", textAlign: "center" }}
            >
                {t("howto.wordGrid.drag-demo-title", "Drag to select a word")}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", maxWidth: 360, lineHeight: 1.6 }}
            >
                {t(
                    "howto.wordGrid.drag-demo-caption",
                    "Press the first letter, then drag across the word — release to mark it found. Try it on the live sample below."
                )}
            </Typography>
            <Box
                sx={{
                    position: "relative",
                    display: "inline-grid",
                    gridTemplateColumns: `repeat(6, ${CELL}px)`,
                    gridTemplateRows: `repeat(6, ${CELL}px)`,
                    gap: `${GAP}px`,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "#f5f5f5",
                    border: "1px solid",
                    borderColor: "divider",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                }}
                aria-hidden
            >
                {DEMO_GRID.map((row, rowIndex) =>
                    row.map((letter, colIndex) => {
                        const routeIndex = ROUTE_CELLS.findIndex(
                            (c) => c.row === rowIndex && c.col === colIndex
                        );
                        const onRoute = routeIndex >= 0;
                        return (
                            <Box
                                key={`${rowIndex}-${colIndex}`}
                                sx={{
                                    width: CELL,
                                    height: CELL,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 0.5,
                                    border: "1px solid #cfd8dc",
                                    bgcolor: onRoute ? "#fff3e0" : "#fff",
                                    fontFamily: "'Open Sans', sans-serif",
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    color: "#263238",
                                    position: "relative",
                                    overflow: "hidden",
                                    ...(onRoute
                                        ? {
                                              "&::after": {
                                                  content: '""',
                                                  position: "absolute",
                                                  inset: 0,
                                                  bgcolor: "#FFB74D",
                                                  borderRadius: 0.5,
                                                  animation: `${sweep} 2.4s ease-in-out infinite`,
                                                  animationDelay: `${routeIndex * 0.18}s`,
                                                  zIndex: 0,
                                              },
                                          }
                                        : null),
                                }}
                            >
                                <Box component="span" sx={{ position: "relative", zIndex: 1 }}>
                                    {letter}
                                </Box>
                            </Box>
                        );
                    })
                )}
                {/* Drag path + finger cue over ROUTE */}
                <Box
                    sx={{
                        position: "absolute",
                        top: pathTop + 8,
                        left: pathLeft + 8,
                        width: pathWidth,
                        height: CELL,
                        pointerEvents: "none",
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: CELL / 2 - 3,
                            left: CELL / 2,
                            right: CELL / 2,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "rgba(255, 152, 0, 0.55)",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: CELL / 2 - 14,
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: "rgba(25, 118, 210, 0.92)",
                            border: "2px solid #fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                            transform: "translateX(-50%)",
                            animation: `${fingerMove} 2.4s ease-in-out infinite`,
                        }}
                    />
                </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t("howto.wordGrid.drag-demo-word", "Example: drag R → E to find ROUTE")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {t("howto.wordGrid.drag-demo-size", "Sample Mini board · 6×6 (we also publish up to 12×12)")}
            </Typography>
        </Box>
    );
}
